import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useIntroComplete } from "@/context/intro-state";

export const Reveal = ({
  as: Component = "div",
  children,
  className,
  delay = 0,
  distance = 28,
  threshold = 0.18,
  once = true,
  variant = "up"
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const introComplete = useIntroComplete();

  useEffect(() => {
    if (!introComplete) {
      setIsVisible(false);
      return;
    }

    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setIsVisible(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: "0px 0px -10% 0px"
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [introComplete, once, threshold]);

  return (
    <Component
      ref={ref}
      className={cn("reveal", `reveal-${variant}`, isVisible && "is-visible", className)}
      style={{
        "--reveal-delay": `${delay}ms`,
        "--reveal-distance": `${distance}px`
      }}
    >
      {children}
    </Component>
  );
};
